import { auth } from '../lib/firebase';

// Côté admin, VITE_BACKEND_URL est déjà utilisée pour le backend TikTok (port 3001).
// On introduit VITE_AUTH_BACKEND_URL pour pointer vers le auth-backend (port 8080),
// qui héberge les endpoints de modération des ingrédients.
const AUTH_BACKEND_URL =
  import.meta.env.VITE_AUTH_BACKEND_URL || 'http://localhost:8080';

async function getIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Vous devez être connecté en admin.');
  return user.getIdToken();
}

async function apiFetch<T>(
  path: string,
  init: { method: 'GET' | 'POST' | 'DELETE'; body?: Record<string, unknown> } = { method: 'GET' }
): Promise<T> {
  const token = await getIdToken();
  const response = await fetch(`${AUTH_BACKEND_URL}${path}`, {
    method: init.method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    ...(init.body ? { body: JSON.stringify(init.body) } : {}),
  });

  let payload: any = {};
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }

  if (!response.ok) {
    const message = payload?.error || payload?.message || `Request failed (${response.status})`;
    const err = new Error(message) as Error & { status?: number; code?: string };
    err.status = response.status;
    err.code = payload?.code;
    throw err;
  }

  return payload as T;
}

// -----------------------------------------------------------------------------

export interface MergeIngredientResponse {
  success: boolean;
  mergedInto: string;
  recipesScanned: number;
  recipesUpdated: number;
  aliasesAdded: number;
}

export interface RejectIngredientResponse {
  success: boolean;
  deletedId: string;
  recipesScanned: number;
  recipesUpdated: number;
}

export interface IngredientUsageRecipe {
  id: string;
  name: string;
  organizationId: string | null;
  isPublic: boolean;
}

export interface IngredientUsageResponse {
  success: boolean;
  count: number;
  recipes: IngredientUsageRecipe[];
}

export async function mergeIngredientApi(
  sourceId: string,
  targetId: string
): Promise<MergeIngredientResponse> {
  return apiFetch<MergeIngredientResponse>(
    `/api/ingredients/${encodeURIComponent(sourceId)}/merge`,
    { method: 'POST', body: { targetId } }
  );
}

export async function rejectIngredientApi(
  ingredientId: string
): Promise<RejectIngredientResponse> {
  return apiFetch<RejectIngredientResponse>(
    `/api/ingredients/${encodeURIComponent(ingredientId)}`,
    { method: 'DELETE' }
  );
}

export async function getIngredientUsageApi(
  ingredientId: string
): Promise<IngredientUsageResponse> {
  return apiFetch<IngredientUsageResponse>(
    `/api/ingredients/${encodeURIComponent(ingredientId)}/usage`,
    { method: 'GET' }
  );
}

// -----------------------------------------------------------------------------

export type RebuildLanguageOption = 'fr' | 'en' | 'all';

export interface CatalogLanguageSummary {
  ok: boolean;
  reason?: string;
  size?: number;
  bytes?: number;
  kb?: number;
  maxBytes?: number;
  version?: number;
}

export interface RebuildCatalogResponse {
  success: boolean;
  scanned: number;
  skipped: number;
  summary: Record<string, CatalogLanguageSummary>;
  refused?: Array<CatalogLanguageSummary & { lang: string }>;
}

/** Triggers a full rebuild of the aggregated catalog document(s) for the
 *  given language (or all supported languages). Admin only — the auth-backend
 *  enforces the role check on its side. */
export async function rebuildIngredientCatalogApi(
  language: RebuildLanguageOption
): Promise<RebuildCatalogResponse> {
  return apiFetch<RebuildCatalogResponse>('/api/ingredients/catalog/rebuild', {
    method: 'POST',
    body: { language },
  });
}
